





































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { mixin as clickaway } from 'vue-clickaway';
import { ConditionGroupViewModel } from './models/conditionGroupViewModel';
import { ConditionGroup } from '@/shared/components/conditions/models/conditionGroup';
import { ConditionViewModel } from './models/conditionViewModel';
import { IConditionsManager } from './models/conditionsManager';
import AddCondition from './AddCondition.vue';
import ConditionButton from './ConditionButton.vue';
import AddIcon from 'vue-material-design-icons/Plus.vue';
import JoinOperatorPicker from '@/features/contacts/components/JoinOperatorPicker.vue';
import { OperatorType } from '@/features/contacts/models/operatorTypes';

export interface ConditionGroupUpdate {
	conditionGroup: ConditionGroupViewModel;
	invalid: boolean;
}

@Component({
	name: 'ConditionGroupBuilder',
	mixins: [
		clickaway
	],
	components: {
		ConditionGroupBuilder,
		ConditionButton,
		AddCondition,
		AddIcon,
		JoinOperatorPicker
	}
})
export default class ConditionGroupBuilder extends Vue {
	@Prop() public value: ConditionGroupViewModel;
	@Prop() public manager: IConditionsManager;
	@Prop({default: false}) public nested: boolean;
	@Prop({default: false}) public disabled: boolean;
	@Prop({default: 'filter'}) public context: string;

	public conditionGroup: ConditionGroupViewModel = { type: 'and', conditions: [] };
	public operatorButtonHover: boolean = false;

	@Watch('value', { immediate: true })
	public onValueChanged(value: ConditionGroupViewModel, previous: ConditionGroupViewModel) {
		this.conditionGroup = value;
	}

	public get numberOfConditions() {
		if (!this.conditionGroup || !this.conditionGroup.conditions) {
			return 0;
		}

		return this.conditionGroup.conditions.length;
	}

	public getConditionKey(index: number, item: ConditionViewModel | ConditionGroupViewModel) {
		if (this.isCondition(item)) {
			return `${index}_${item.attribute}_${item.operation}_${item.value}`;
		}

		return `${index}_group`;
	}

	public isLastConditionInList(index: number) {
		return index === this.numberOfConditions - 1;
	}

	public isCondition(item: ConditionViewModel | ConditionGroupViewModel): item is ConditionViewModel  {
		return ConditionGroup.isCondition(item);
	}

	public showMakeFilterGroupOption(item: ConditionViewModel | ConditionGroupViewModel) {
		return !this.nested && this.isCondition(item);
	}

	public showAddToFilterGroupOption(item: ConditionViewModel | ConditionGroupViewModel, index: number) {
		return this.nested && this.isCondition(item) && index === this.numberOfConditions - 1;
	}

	public onConditionAdded(newItem: ConditionViewModel) {
		newItem.active = true;
		this.conditionGroup.conditions.push(newItem);
	}

	public onConditionUpdated(index: number, item: ConditionViewModel) {
		item.active = false;
		this.$set(this.conditionGroup.conditions, index, item);

		this.emitUpdatedEvent();
	}

	public onConditionDeleted(index: number) {
		this.conditionGroup.conditions.splice(index, 1);

		this.emitUpdatedEvent();
	}

	public makeFilterGroup(index: number, item: ConditionViewModel, newItem: ConditionViewModel) {
		// replace the condition with a condition group containing existing and new item
		newItem.active = true;
		this.$set(this.conditionGroup.conditions, index, {
			type: 'and',
			conditions: [
				item,
				newItem
			]
		} as ConditionGroupViewModel);
	}

	public addToFilterGroup(index: number, newItem: ConditionViewModel) {
		newItem.active = true;
		this.conditionGroup.conditions.push(newItem);
	}

	public onGroupUpdated() {
		// a child group has been updated - check if any groups have only got one condition left,
		// if so convert them back to a condition from a filter group
		const newList = this.conditionGroup.conditions.map((c, index) => {
			if (this.isCondition(c)) {
				c.active = false;
				return c;
			}

			c = c as ConditionGroupViewModel;
			if (c.conditions.length === 1) {
				return c.conditions[0];
			}

			return c;
		});

		this.conditionGroup.conditions = newList;
		this.emitUpdatedEvent();
	}

	public operatorTypeUpdated(operatorType: OperatorType) {
		this.conditionGroup.type = operatorType;
		this.onGroupUpdated();
	}

	public updateOperatorHoverState(hover: boolean) {
		this.operatorButtonHover = hover;
	}

	public hasAnyInvalidConditions() {
		return ConditionGroup.isGroupInvalid(this.conditionGroup);
	}

	private emitUpdatedEvent() {
		if (this.nested) {
			this.$emit('updated', this.conditionGroup);
		} else {
			this.$emit('updated', {
				conditionGroup: this.conditionGroup,
				invalid: this.hasAnyInvalidConditions()
			} as ConditionGroupUpdate);
		}
	}
}
